import { getItemFromLocalStorage, getItemFromSessionStorage } from '@/helpers/localStorageServices';

/**
 * bpSelection — the synchronous, per-tab source of truth for the CURRENT business-profile
 * selection intent (the internal businessProfileId, NOT the Amazon profileId/marketplace).
 *
 * Why this exists: token/BP resolution (tokenService.resolveBusinessProfileId,
 * auth.resolveBpId) needs to know "which BP does this request target" SYNCHRONOUSLY — on a
 * profile switch the new BP must be visible to in-flight requests before the store updates,
 * and on a hard refresh it must be known before the router/store are ready. Historically that
 * synchronous source was `window.location.search?businessProfileId`, which forced every URL
 * write to be a synchronous awaited router.replace — incompatible with the URL coordinator
 * (urlState) that coalesces and defers query writes.
 *
 * This module decouples the two: the INTENT lives here (synchronous, in-memory), and the URL
 * becomes a mere reflection written via urlState. Readers consult get() first; the URL is no
 * longer read on the hot path. Seeded once at bootstrap from the only sources available before
 * the router resolves — the URL query, then persisted selection.
 *
 * Deliberately dependency-free (no Pinia, no router, no urlState) so the auth-critical
 * tokenService can import it without import cycles. The URL reflection (urlState.patchQuery)
 * is orchestrated by account.js, not here.
 *
 * In-memory and per-tab on purpose: each tab owns its own selection. A fresh tab is seeded
 * from storage; a hard refresh from the URL.
 */

let currentBpId = null;

// Current intended businessProfileId as a string, or null when nothing is selected yet.
function get() {
  return currentBpId;
}

// Record the selection intent synchronously. Pass null/'' to clear.
function set(id) {
  currentBpId = id != null && id !== '' ? String(id) : null;
}

// Seed once at app start, BEFORE the first BP-resolving request. Reads the synchronous
// pre-router sources: the URL query first (hard-refresh / shared link), then the persisted
// selection (fresh tab). Does not overwrite an already-set value.
function seedFromEnvironment() {
  if (currentBpId != null) return;
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('businessProfileId');
    if (fromUrl) {
      currentBpId = String(fromUrl);
      return;
    }
  } catch {
    /* window unavailable — fall through to storage */
  }
  const stored =
    getItemFromSessionStorage('currentBusinessProfile')?.id ??
    getItemFromLocalStorage('currentBusinessProfile')?.id;
  if (stored != null) currentBpId = String(stored);
}

export const bpSelection = { get, set, seedFromEnvironment };
